import AppConfig from '@/config'
import { authHeader } from '@/constants/storageKeys'
import { getInterceptorHooks } from './http'
import { ApiError, apiErrorFromResponse, classifyLimit } from './errors'
import { redirectToAuth } from '@/utils/authRedirect'
import { guestHeader } from '@/utils/guestId'

export interface PostSSEInit {
	body: BodyInit
	signal: AbortSignal
	headers?: Record<string, string>
	/** Точка, где эндпоинт может уточнить разобранную ошибку (например, проставить код). */
	onNotOk?: (error: ApiError) => void | Promise<void>
}

export async function postSSE(
	path: string,
	init: PostSSEInit
): Promise<ReadableStreamDefaultReader<Uint8Array> | null> {
	const hooks = getInterceptorHooks()
	let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
	try {
		const response = await fetch(`${AppConfig.apiUrl}${path}`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				...authHeader(),
				...guestHeader(),
				...init.headers
			},
			body: init.body,
			signal: init.signal
		})

		// Стриминг идёт через fetch мимо axios-интерцептора, поэтому те же реакции повторяем здесь.
		// Тело читаем один раз и только у неуспешного ответа: у успешного его забирает getReader().
		if (!response.ok) {
			const apiError = await apiErrorFromResponse(response)
			if (response.status === 401) {
				await hooks.onUnauthorized()
				redirectToAuth()
			}
			const limit = classifyLimit(apiError)
			// Строго до onNotOk: тот может бросить и тогда реакция на лимит не сработает.
			if (limit) {
				hooks.onLimitExceeded(limit, apiError.resource)
			}
			if (init.onNotOk) {
				await init.onNotOk(apiError)
			}
			throw apiError
		}

		if (!response.body) {
			throw new Error('Response body is null or undefined')
		}

		reader = response.body.getReader()
		return reader
	} catch (error: unknown) {
		if (error instanceof TypeError) {
			hooks.onNetworkError()
		}
		if (error instanceof DOMException && error.name === 'AbortError') {
			if (reader) {
				reader.releaseLock()
			}
			return null
		}
		// Исчерпанный лимит — ожидаемый ответ, а не сбой: интерфейс на него уже отреагировал.
		if (!(error instanceof ApiError && classifyLimit(error))) {
			console.error('Error in postSSE:', error)
		}
		throw error
	}
}

export function extractData(event: string): string {
	return event
		.split('\n')
		.filter((line) => line.startsWith('data:'))
		.map((line) => line.slice('data:'.length).trim())
		.join('')
}

export interface ParseSSEOptions {
	onData: (data: any) => void
	shouldStop: () => boolean
}

export async function parseSSE(
	reader: ReadableStreamDefaultReader<Uint8Array>,
	options: ParseSSEOptions
): Promise<void> {
	const { onData, shouldStop } = options
	let buffer = ''
	while (!shouldStop()) {
		const { done, value } = await reader.read()
		if (done || shouldStop()) break

		buffer += new TextDecoder().decode(value)

		// SSE-события разделены двойным переводом строки
		let boundary: number
		while ((boundary = buffer.indexOf('\n\n')) !== -1) {
			const event = buffer.slice(0, boundary)
			buffer = buffer.slice(boundary + 2)
			const dataLine = extractData(event)
			if (dataLine) {
				try {
					onData(JSON.parse(dataLine))
				} catch (e) {
					console.error('Error parsing stream data:', e)
				}
			}
		}
	}

	if (buffer.trim()) {
		const dataLine = extractData(buffer)
		if (dataLine) {
			try {
				onData(JSON.parse(dataLine))
			} catch (e) {
				console.error('Error parsing remaining stream data:', e)
			}
		}
	}
}
